import { useEffect, useState, useEffectEvent } from 'react'
import type { FC } from 'react'
import { useTypedWorklistSelector, useWorklistDispatch } from './store'
import { worklistPreferencesSlice } from './preferencesSlice'
import { WorklistCategory } from './WorklistTree/WorklistTreeCategory'
import groupBy from 'lodash/fp/groupBy'
import { SESSION_WORKLIST_PREFERENCES, setPreferences } from './preferenceStorage'
import { useWorkLists } from './WorkListsService'
import { haveChanged } from '~/shared/lib/haveChanged'
import { sortedEntries } from '~/shared/lib/sortedEntries'
interface WorklistSidebarProps {
  worklistId: number | undefined
  setWorklistId: (worklistId: number) => void
}

const WorklistSidebar: FC<WorklistSidebarProps> = (props: WorklistSidebarProps) => {
  const { result: worklists } = useWorkLists()
  const worklistPreferences = useTypedWorklistSelector(state => state.worklistPreferences)
  const [expandingNodes, setExpandingNodes] = useState<string[]>([])
  const worklistDispatch = useWorklistDispatch()

  // Auto-selects a worklist when the list arrives: the last viewed one if it is still active,
  // else the first active one. `lastViewedWorklistId` is written by the selection this makes and
  // `props` is rebuilt by every parent render, so both are read through the event, not depended on.
  const autoSelectWorklist = useEffectEvent(() => {
    if (worklists && Object.values(worklists).length > 0) {
      const activeWorklists = Object.values(worklists).filter(worklist => worklist.active)
      const [firstActiveWorklist] = activeWorklists
      const lastViewedWorklistId = worklistPreferences.lastViewedWorklistId
      const hasActiveLastViewedWorklistId =
        lastViewedWorklistId != null &&
        activeWorklists.some(worklist => worklist.id === lastViewedWorklistId)
      if (!props.worklistId && hasActiveLastViewedWorklistId) {
        props.setWorklistId(lastViewedWorklistId)
      } else if (!props.worklistId && firstActiveWorklist) {
        props.setWorklistId(firstActiveWorklist.id)
      }
    }
  })
  useEffect(() => {
    autoSelectWorklist()
  }, [worklists])

  // Used for auto saving worklist preferences
  useEffect(() => {
    setPreferences(JSON.stringify(worklistPreferences), SESSION_WORKLIST_PREFERENCES)
  }, [worklistPreferences])

  useEffect(() => {
    if (props.worklistId) {
      worklistDispatch(worklistPreferencesSlice.actions.setLastViewedWorklistId(props.worklistId))
    }
  }, [props.worklistId, worklistDispatch])

  // The selected worklist's group expands whenever the selection or the list changes.
  const selectionSource = [props.worklistId, worklists]
  const [expandedFor, setExpandedFor] = useState<unknown[]>()
  if (haveChanged(expandedFor, selectionSource)) {
    setExpandedFor(selectionSource)
    const selectedWorklist = Object.values(worklists ?? {}).find(
      worklist => worklist.id === props.worklistId,
    )
    if (selectedWorklist) {
      setExpandingNodes(nodes => [
        ...nodes,
        selectedWorklist.category + ' ' + selectedWorklist.sub_category,
      ])
    }
  }

  const categories = groupBy('category', worklists ?? [])
  return (
    <>
      {sortedEntries(categories).map(([category, categoryItems]) => (
        <WorklistCategory
          key={category}
          category={category}
          categoryItems={categoryItems}
          expandingNodes={expandingNodes}
          setExpandingNodes={setExpandingNodes}
          selectWorklist={props.setWorklistId}
          worklistId={props.worklistId}
        ></WorklistCategory>
      ))}
    </>
  )
}
export default WorklistSidebar
